/*
* Copyright  2025-2026, Airspan
* All Rights Reserved
*
* This code is unpublished, confidential and proprietary to Airspan and may only be used in accordance with the appropriate
* license agreement. All other copying, modification, distribution, or
* reverse engineering are strictly prohibited.
*/

import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import CustomDateTimePicker from "../DateTimePicker/DateTimePicker";
import SearchBox from "../SearchBox/SearchBox";
import { useDispatch, useSelector } from "react-redux";
import { IFilter } from "../Filter/types";
import { FilterX } from "lucide-react";
import { resetKeyword } from "../../../store/search/reducer";
import { searchKeywordSelector } from "../../../store/search/selector";
import { useRegionDisplay } from "../../../pages/Administrations/AccessControls/Regions/hooks";
import { RadixSelectControl } from "../../ui/radix-select";
import { Button } from "../../ui/button";
import { Tooltip } from "@mui/material";

const AssociationFilter: React.FC<IFilter> = ({
  searchTooltip = "",
  selectFilters = [],
  dateFilters = [],
  toggleButtonFilters = [],
  resetFilters,
  searchPlaceholder = "Search...",
}) => {
  const { geoRegions } = useRegionDisplay();
  const dispatch = useDispatch();
  const keyword = useSelector(searchKeywordSelector);

  const hasActiveFilters =
    (keyword && keyword.length > 0) ||
    dateFilters.length > 0 ||
    toggleButtonFilters.length > 0 ||
    selectFilters.some((filter) => {
      if (filter.multiple && Array.isArray(filter.value) && filter.value.length > 0) return true;
      if (!filter.multiple && typeof filter.value === "string" && filter.value !== "") return true;
      return false;
    });

  const handleResetFilter = (resetFiltersAction: any) => {
    dispatch(resetFiltersAction());
    dispatch(resetKeyword());
  };

  const handleOnChange = (filter: any, value: string) => {
    if (filter.id === "geoRegion" || filter.id === "addDeviceGeoRegion") {
      const isExist = geoRegions.find((o: any) => o.objectKey === value);
      const name = isExist ? isExist.name : "";
      dispatch(filter.setValue({ name: name, objectKey: value || "" }));
    } else {
      dispatch(filter.setValue(value));
    }
  };

  const [toggleButtonValue, setToggleButtonValue] = useState("");

  useEffect(() => {
    if (toggleButtonFilters.length > 0 && toggleButtonValue === "") {
      const initialValue = toggleButtonFilters[0].value || "";
      setToggleButtonValue(initialValue);
      toggleButtonFilters[0].onClick?.(initialValue);
    }
  }, [toggleButtonFilters, toggleButtonValue]);


  return (
    <div className="flex items-center gap-2">
      <SearchBox width="240px" tooltip={searchTooltip} placeholder={searchPlaceholder} />
      {selectFilters.map((filter) => (
        <div key={filter.id} style={{ width: filter?.width || "130px" }}>
          <RadixSelectControl
            value={filter.value ?? "__placeholder__"}
            onValueChange={(val) =>
              handleOnChange(filter, val === "__placeholder__" ? "" : val)
            }
            options={
              typeof filter.values[0] === "object"
                ? [
                  { value: "__placeholder__", label: (filter.label) },
                  ...filter.values.map((item: any) => ({
                    value: item?.value,
                    label: (item?.text),
                  })),
                ]
                : ["__placeholder__", ...filter.values].map((item: any, idx: number) =>
                  typeof item === "object"
                    ? { value: item.value, label: (item.text) }
                    : {
                      value: item,
                      label: idx === 0 ? (filter.label) : (item),
                    }
                )
            }
            className="capitalize-text"
            placeholder={(filter.label)}
            contentWidth="130px"
          />
        </div>
      ))}

      {dateFilters.length > 0 &&
        dateFilters.map((filter) => (
          <CustomDateTimePicker
            key={filter.label}
            maxDateTime={filter.max || dayjs()}
            label={filter.label}
            value={filter.value || dayjs().subtract(1, "day")}
            handleOnChange={filter.onChange}
          />
        ))}

      {toggleButtonFilters.length > 0 && (
        <div className="inline-flex rounded-md border border-border overflow-hidden h-8">
          {toggleButtonFilters.map((filter) => (
            <button
              key={filter.id ?? filter.value}
              onClick={() => {
                const value = filter.value || "";
                setToggleButtonValue(value);
                filter.onClick?.(value);
              }}
              className={`px-3 text-sm font-medium border-r last:border-r-0 border-border transition-colors ${toggleButtonValue === filter.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-transparent text-muted-foreground hover:bg-accent"
                }`}
            >
              {filter.name || ""}
            </button>
          ))}
        </div>
      )}
      {hasActiveFilters && (
        <Tooltip title="Reset All">
          <span>
            <Button
              variant="outline"
              size="sm"
              className="h-6 w-6 shrink-2"
              onClick={() => handleResetFilter(resetFilters)}
              aria-label="Reset All"
            >
              <FilterX className="h-4 w-4" />
            </Button>
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default AssociationFilter;